import Constants from "../constants";
import Fund from "../fund";
import FundAllocation from "../fundAllocation";
import fundInfo from "../funds";
import { roundDown, roundToDecimalPlaces } from "../utilities";
import FundAllocationCalculation from "./fundAllocationCalculation";

export default class FundAllocationCalculator {
    public static TrusteeAccountPercentage = 1;

    public static calculate(pensionPotsTotalValue: number, pensionPotDrawdownAmounts: Array<number>, isAlreadyRetired: boolean, isTakingLumpSum: boolean, lumpSumMoneyFundPercentage: number, 
        numberOfYearsHeldInMatchingIncome: number): FundAllocationCalculation {
        let moneyFundPercentage: number = FundAllocationCalculator.getMoneyFundPercentage(pensionPotsTotalValue, isAlreadyRetired, isTakingLumpSum, lumpSumMoneyFundPercentage);
        let incomeFundAmount = 0;
    
        if (numberOfYearsHeldInMatchingIncome > 0)
        {
            for (let matchedYear = 0; matchedYear < numberOfYearsHeldInMatchingIncome; matchedYear++) {
                incomeFundAmount += pensionPotDrawdownAmounts[matchedYear];
            }
        }

        const incomeFundPercentage = roundToDecimalPlaces((incomeFundAmount / pensionPotsTotalValue) * 100, 1);
        const growthFundPercentage = roundToDecimalPlaces(100 - this.TrusteeAccountPercentage - moneyFundPercentage - incomeFundPercentage, 1);

        const allocations = new Array<FundAllocation>();

        allocations.push(FundAllocationCalculator.createFundAllocation(fundInfo.trusteeBankAccount, this.TrusteeAccountPercentage, pensionPotsTotalValue));
        allocations.push(FundAllocationCalculator.createFundAllocation(fundInfo.moneyFund, moneyFundPercentage, pensionPotsTotalValue));
        allocations.push(new FundAllocation(fundInfo.incomeFund, incomeFundPercentage, incomeFundAmount));
        
        const alreadyAllocated = roundToDecimalPlaces(allocations.reduce((sum, a) => sum + a.amount, 0), 0);
        const remainingForAllocation = roundToDecimalPlaces(pensionPotsTotalValue - alreadyAllocated, 0);
        const growthFundAlloc = new FundAllocation(fundInfo.responsibleGrowthFund, growthFundPercentage, remainingForAllocation);
        allocations.push(growthFundAlloc);

        return new FundAllocationCalculation(allocations);
    }

    private static createFundAllocation(fund: Fund, percentage: number, total: number): FundAllocation {
        const amount = FundAllocationCalculator.calcAmountFromPercentage(total, percentage);
        return new FundAllocation(fund, percentage, amount);
    }
    
    private static calcAmountFromPercentage(total: number, percentage: number): number {
        if (percentage < 0 || percentage > 100) {
            throw new Error("Percentage must be between 0 and 100");
        }
    
        const decimalPercentage = percentage / 100;
    
        const amount = total * decimalPercentage;
    
        function bankersRound(num: number): number {
            let integer = Math.floor(num);
            let fraction = num - integer;
            
            if (fraction < 0.5) {
                return integer;
            } else if (fraction > 0.5) {
                return integer + 1;
            } else {
                return integer % 2 === 0 ? integer : integer + 1;
            }
        }
    
        return bankersRound(amount);
    }

    private static getMoneyFundPercentage(pensionPotsTotalValue: number, isAlreadyRetired: boolean, isTakingLumpSum: boolean, 
        lumpSumMoneyFundPercentage: number): number {

        if (!FundAllocationCalculator.isMoneyFundLumpSumApplicable(isAlreadyRetired, isTakingLumpSum, lumpSumMoneyFundPercentage)) {
            return 0;
        }

        const amount = this.getLumpSumAmountToAllocateToMoneyFund(pensionPotsTotalValue, isAlreadyRetired, isTakingLumpSum, lumpSumMoneyFundPercentage);

        return roundToDecimalPlaces((amount / pensionPotsTotalValue) * 100, 1);
    }

    private static getLumpSumAmountToAllocateToMoneyFund(pensionPotsTotalValue: number, isAlreadyRetired: boolean, isTakingLumpSum: boolean, 
        lumpSumMoneyFundPercentage: number): number {

        if (!FundAllocationCalculator.isMoneyFundLumpSumApplicable(isAlreadyRetired, isTakingLumpSum, lumpSumMoneyFundPercentage)) {
            return 0;
        }

        let amount = pensionPotsTotalValue * (lumpSumMoneyFundPercentage / 100);
        amount = roundDown(amount, 10);
        amount = Math.min(amount, Constants.TaxFreeCashLimit);
        return amount;
    }

    private static isMoneyFundLumpSumApplicable(isAlreadyRetired: boolean, isTakingLumpSum: boolean, lumpSumMoneyFundPercentage: number): boolean {
        return !isAlreadyRetired && isTakingLumpSum && lumpSumMoneyFundPercentage > 0;
    }
}
